import { useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { getPriorityColor } from '../../../lib/utils';
import { Typography } from '@mui/material';

const AGING_CATEGORIES = [
  '<15 days',
  '15-30 days',
  '30-60 days',
  '60-100 days',
  '100+ days',
];

// const AGING_COLORS = {
//   '<15 days': '#27ae60',
//   '15-30 days': '#f1c40f',
//   '30-60 days': '#e67e22',
//   '60-100 days': '#c0392b',
//   '100+ days': '#6c3483',
// };

const capitalize = (str) =>
  typeof str === 'string'
    ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
    : str;

const extractUniquePriorities = (projects = []) => {
  const unique = new Set();
  projects.forEach((project) => {
    (project.defect_aging || []).forEach((bucket) => {
      Object.keys(bucket.priorities || {}).forEach((priority) => {
        unique.add(capitalize(priority));
      });
    });
  });
  return Array.from(unique);
};

// const transformChartData = (projects = []) =>
//   projects.map((project) => {
//     const base = {
//       key: project.project_key,
//       project: project.project_name,
//       prioritiesByAging: {},
//       jiraUrls: {},
//       is_jira_integrated: project.is_jira_integrated || false,
//     };

//     AGING_CATEGORIES.forEach((cat) => {
//       base[cat] = 0;
//       base.prioritiesByAging[cat] = {};
//     });

//     (project.defect_aging || []).forEach((bucket) => {
//       const { category, total, priorities = {}, jira_url } = bucket;
//       if (AGING_CATEGORIES.includes(category)) {
//         base[category] = total || 0;
//         Object.entries(priorities).forEach(([p, count]) => {
//           base.prioritiesByAging[category][capitalize(p)] = count || 0;
//         });
//         base.jiraUrls[category] = jira_url;
//       }
//     });

//     return base;
//   });

// const buildMiniChartData = (project, priorities) =>
//   AGING_CATEGORIES.map((aging) => {
//     const row = {
//       aging,
//       total: project[aging] || 0,
//       jira_url: project?.jiraUrls?.[aging],
//     };
//     priorities.forEach((p) => {
//       row[p] = project.prioritiesByAging?.[aging]?.[p] || 0;
//     });
//     return row;
//   });

const AgingOverviewTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const { aging, total, jira_url, ...rest } = payload[0].payload || {};
  const priorities = Object.entries(rest)
    .filter(([key]) => key !== 'aging' && key !== 'total')
    .sort(([a], [b]) => a.localeCompare(b));

  return (
    <div
      className="bg-white border p-2 rounded shadow-sm"
      style={{ fontSize: 14, minWidth: 180 }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
        {aging}
      </div>

      <div className="d-flex align-items-center mb-1">
        <div
          style={{
            width: 10,
            height: 10,
            backgroundColor: '#6664d8',
            borderRadius: 2,
            marginRight: 6,
          }}
        />
        <span style={{ flex: 1 }}>Total</span>
        <span>{total || 0}</span>
      </div>

      {priorities.map(([priority, count]) => (
        <div key={priority} className="d-flex align-items-center mb-1">
          <div
            style={{
              width: 10,
              height: 10,
              backgroundColor: getPriorityColor(priority),
              borderRadius: 2,
              marginRight: 6,
            }}
          />
          <span style={{ flex: 1 }}>{priority}</span>
          <span>{count}</span>
        </div>
      ))}

      {total > 0 && jira_url && (
        <div className="text-muted mt-1" style={{ fontSize: 12 }}>
          Click bar to open issues in JIRA →
        </div>
      )}
    </div>
  );
};

const DefectAgingOrgMetrics = ({ data, isLoading }) => {
  const projects = data?.data?.projects || [];
  const overallAging = data?.data?.overall?.defect_aging || [];

  // const chartData = useMemo(() => transformChartData(projects), [projects]);
  const priorities = useMemo(
    () => extractUniquePriorities(projects),
    [projects]
  );

  // const [selectedProject, setSelectedProject] = useState(
  //   () => chartData?.[0] || null
  // );

  const buildOverallChartData = useMemo(() => {
    return AGING_CATEGORIES.map((category) => {
      const entry = overallAging.find((d) => d.category === category) || {};
      const row = {
        aging: category,
        total: entry.total || 0,
        jira_url: entry.jira_url,
      };
      priorities.forEach((p) => {
        const key = capitalize(p);
        row[key] = entry?.[key.toLowerCase()] || 0;
      });
      return row;
    });
  }, [overallAging, priorities]);

  // const selectedHasDefects = useMemo(() => {
  //   return (
  //     selectedProject &&
  //     AGING_CATEGORIES.some((cat) => selectedProject?.[cat] > 0)
  //   );
  // }, [selectedProject]);

  // const selectedHasJira = !!selectedProject?.is_jira_integrated;
  return (
    <>
      <Row className="mb-4">
        <Col md={12} style={{ height: 400 }}>
          <Typography variant="h5" className="my-3">Defect Aging</Typography>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={buildOverallChartData}
              margin={{ top: 20, right: 10, left: 10, bottom: 20 }}
              barCategoryGap="20%"
              onClick={(e) => {
                const payload = e?.activePayload?.[0]?.payload;
                const agingCategory = payload?.aging;
                const total = payload?.total;
                if (!agingCategory || total === 0) return;
                const matched = overallAging.find(
                  (d) => d.category === agingCategory
                );

                const url = matched?.jira_url;
                if (url) window.open(url, '_blank', 'noopener,noreferrer');
              }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="aging" interval={0} height={60} />
              <YAxis
                allowDecimals={false}
                label={{
                  value: 'Defects',
                  angle: -90,
                  position: 'insideLeft',
                  fontSize: 14,
                }}
                tick={{ fontSize: 12 }}
              />
              <Tooltip content={<AgingOverviewTooltip />} />
              <Legend verticalAlign="top" height={30} />

              <Bar
                dataKey="total"
                name="Total"
                fill="#6664d8"
                stackId="group1"
                barSize={20}
              />

              {priorities.map((priority) => (
                <Bar
                  key={priority}
                  dataKey={priority}
                  name={priority}
                  stackId="group2"
                  fill={getPriorityColor(priority)}
                  barSize={20}
                  isAnimationActive={false}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Col>
      </Row>

      {/* <Row className="mb-4">
        <Col md={12}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Typography variant="h5">Defect Aging by Project</Typography>
            <select
              className="form-select w-auto"
              value={selectedProject?.key || chartData[0]?.key}
              onChange={(e) => {
                const selected = chartData.find(
                  (p) => p.key === e.target.value
                );
                if (selected) setSelectedProject(selected);
              }}>
              {chartData.map((project) => (
                <option key={project.key} value={project.key}>
                  {project.project?.length > 40 ? `${project.project.slice(0, 40)}...` : project.project}
                </option>
              ))}
            </select>
          </div>
        </Col>
        {!selectedHasJira ? (
          <div className="alert alert-warning text-center my-4">
            This project is not linked to any JIRA project.
          </div>
        ) : !selectedHasDefects ? (
          <Col md={12} className="mt-4">
            <div className="alert alert-info text-center">
              No defect data available for this project.
            </div>
          </Col>
        ) : (
          <>
            <Col md={6} style={{ height: 400 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Tooltip />
                  <Legend />
                  <Pie
                    dataKey="value"
                    isAnimationActive={false}
                    data={AGING_CATEGORIES.map((cat) => ({
                      name: cat,
                      value: selectedProject?.[cat] || 0,
                      fill: AGING_COLORS[cat],
                    })).filter((item) => item.value > 0)}
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={120}>
                    {AGING_CATEGORIES.map((cat, index) => (
                      <Cell key={`cell-${index}`} fill={AGING_COLORS[cat]} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </Col>

            <Col md={6} style={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={buildMiniChartData(selectedProject, priorities)}
                  margin={{ top: 10, right: 10, bottom: 30, left: 10 }}
                  onClick={(e) => {
                    const payload = e?.activePayload?.[0]?.payload;
                    const agingCategory = payload?.aging;
                    const total = payload?.total;
                    if (!agingCategory || total === 0) return;
                    const url = selectedProject?.jiraUrls?.[agingCategory];
                    if (url) window.open(url, '_blank', 'noopener,noreferrer');
                  }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="aging"
                    label={{
                      value: 'Aging',
                      position: 'insideBottom',
                      offset: -5,
                    }}
                  />
                  <YAxis allowDecimals={false} width={30} />
                  <Tooltip content={<AgingOverviewTooltip />} />
                  <Legend verticalAlign="top" height={30} />
                  {priorities.map((priority) => (
                    <Bar
                      key={priority}
                      dataKey={priority}
                      stackId="a"
                      fill={getPriorityColor(priority)}
                      isAnimationActive={false}
                      barSize={30}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Col>
          </>
        )}
      </Row> */}
    </>
  );
};

export default DefectAgingOrgMetrics;
